import type { RoomView } from '@v2/multiplayer'
import { Icon } from '@v2/react'
import type { ConnectionStatus, RoomConnection } from './network'
import { CopyLink } from './Lobby'

export function MemberList({ room, connection, disabled }: { room: RoomView; connection: RoomConnection; disabled: boolean }) {
  const leader = room.you?.leader ?? false
  return <ul className="pg-members" aria-label="Members">{room.members.map(member => {
    const you = member.id === room.you?.memberId
    return <li key={member.id} className={`pg-member${you ? ' is-you' : ''}`}>
      <span className={`pg-presence${member.connected ? ' is-on' : ''}`} title={member.connected ? 'Connected' : 'Away'} aria-hidden="true"/>
      <span className="pg-member-name">{member.name}{you && <small> you</small>}</span>
      {member.kind === 'bot' && <span className="pg-tag"><Icon name="bot" size={12}/>bot</span>}
      {member.leader && <span className="pg-tag" title="Table leader"><Icon name="crown" size={12}/>leader</span>}
      {leader && !you && room.phase !== 'playing' && <button type="button" className="tt-icon-button pg-remove" aria-label={`Remove ${member.name}`} disabled={disabled} onClick={() => { void connection.remove(member.id).catch(() => {}) }}><Icon name="close" size={14}/></button>}
    </li>
  })}</ul>
}

/** Everything about the room that is not the game: people, the invite, bots, the way back. */
export function RoomPanel({ room, connection, status, pending }: { room: RoomView; connection: RoomConnection; status: ConnectionStatus; pending: boolean }) {
  const leader = room.you?.leader ?? false
  const disabled = pending || status !== 'connected'
  const bots = room.members.some(member => member.kind === 'bot')
  const control = (action: Parameters<RoomConnection['control']>[0]) => { void connection.control(action).catch(() => {}) }
  return <div className="pg-panel">
    <MemberList room={room} connection={connection} disabled={disabled}/>
    <div className="pg-panel-row"><code className="pg-code-inline">{room.code}</code><CopyLink url={room.inviteUrl} compact/></div>
    {bots && <section className="pg-panel-section" aria-label="Bots">
      <h3>Bots</h3>
      {leader && <div className="pg-panel-actions">
        {room.takeover ? <button className="tt-secondary-button" disabled={disabled} onClick={() => control('/bots/release')}>Give the turn back</button> : <>
          {room.bot.state === 'paused' ? <button className="tt-secondary-button" disabled={disabled} onClick={() => control('/bots/retry')}>Resume bots</button> : <button className="tt-secondary-button" disabled={disabled} onClick={() => control('/bots/pause')}>Pause bots</button>}
          {room.bot.state !== 'idle' && <button className="tt-secondary-button" disabled={disabled} onClick={() => control('/bots/takeover')}>Play this turn</button>}
        </>}
      </div>}
      <p className="pg-muted pg-small">{room.bot.decisions.toLocaleString()} of {room.bot.decisionLimit.toLocaleString()} decisions · {room.bot.totalTokens.toLocaleString()} tokens</p>
    </section>}
    <div className="pg-panel-actions">
      {leader && <button className="tt-secondary-button" disabled={disabled} onClick={() => control('/lobby')}><Icon name="reset" size={14}/>{room.phase === 'complete' ? 'Play again' : 'Back to the lobby'}</button>}
      {!room.you?.playerId && <button className="tt-secondary-button" disabled={pending} onClick={() => { void connection.control('/leave').then(() => location.assign('/')).catch(() => {}) }}>Leave the table</button>}
    </div>
    {room.you?.playerId && !leader && <p className="pg-muted pg-small">You have a seat, so you can leave once the game ends or the leader returns to the lobby.</p>}
  </div>
}
